import useSWR from "swr";
import { ArrowRight, Home, LayoutGrid, MapPin } from "lucide-react";

import api from "../../../../lib/api";
import type { CourtComplexRow, CourtRoomRow, DistrictRow, EstablishmentRow } from "./types";
import {
  BackButton,
  DataTable,
  DetailHeader,
  MobileInfoRow,
  OrangeIconBox,
  PrimaryButton,
  RowActions,
  StatusBadge,
  rowClassName,
} from "./DistrictCourtUi";

interface CourtEstablishmentsProps {
  district: DistrictRow;
  complex: CourtComplexRow;
  establishments: EstablishmentRow[];
  isLoading: boolean;
  onBack: () => void;
  onAdd: () => void;
  onOpen: (establishment: EstablishmentRow) => void;
  onEdit: (establishment: EstablishmentRow) => void;
  onDelete: (establishment: EstablishmentRow) => void;
}

const getRows = <T,>(response: any): T[] => {
  const payload = response?.data;

  if (payload?.status === false) return [];
  return (payload?.data || payload || []) as T[];
};

const plural = (count: number, label: string) =>
  `${count} ${label}${count === 1 ? "" : "s"}`;

const locationSummary = (complex: CourtComplexRow) => {
  const address = complex.address || "";
  const parts = address
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length >= 2) {
    const last = parts[parts.length - 1].replace(/\s+\d{5,6}$/, "");
    return `${parts[parts.length - 2]}, ${last}`;
  }

  return address || "N/A";
};

const knownEstablishmentTypes = [
  "District & Sessions Court",
  "Additional District & Sessions Court",
  "Chief Judicial Magistrate (CJM) Court",
  "Additional Chief Judicial Magistrate (ACJM) Court",
  "Judicial Magistrate First Class (JMFC) Court",
  "Sub-Divisional Judicial Magistrate (SDJM) Court",
  "Executive Magistrate Court",
  "Principal District Judge Court",
  "Additional District Judge Court",
  "Civil Judge (Senior Division) Court",
  "Civil Judge (Junior Division) Court",
  "Munsiff Court",
  "Family Court",
  "Fast Track Court",
  "Motor Accident Claims Tribunal (MACT)",
  "Labour Court",
];

const normalizeText = (value?: string) =>
  (value || "").trim().toLowerCase().replace(/\s+/g, " ");

const findKnownEstablishmentType = (value?: string) => {
  const normalized = normalizeText(value);

  if (!normalized) return "";
  return (
    knownEstablishmentTypes.find(
      (type) => normalizeText(type) === normalized,
    ) || ""
  );
};

const legacyTypeLabel = (value?: string) => {
  switch (value) {
    case "DISTRICT_COURT":
      return "District & Sessions Court";
    case "SESSION_COURT":
      return "District & Sessions Court";
    case "TRIBUNAL":
      return "Family Court";
    case "HIGH_COURT":
      return "Principal District Judge Court";
    default:
      return "";
  }
};

const formatType = (establishment: EstablishmentRow) =>
  establishment.establishmentType ||
  findKnownEstablishmentType(establishment.code) ||
  legacyTypeLabel(establishment.type) ||
  "Establishment";

const presidingOfficer = (establishment: EstablishmentRow) =>
  establishment.presidingOfficerName ||
  establishment.presidingOfficer ||
  "Not assigned";

export const CourtEstablishments = ({
  district,
  complex,
  establishments,
  isLoading,
  onBack,
  onAdd,
  onOpen,
  onEdit,
  onDelete,
}: CourtEstablishmentsProps) => {
  const { data: courtRoomCounts = {} } = useSWR(
    establishments.length
      ? `new-courts-establishment-room-counts:${establishments
          .map((establishment) => establishment._id)
          .join("|")}`
      : null,
    async () => {
      const entries = await Promise.all(
        establishments.map(async (establishment) => {
          const response = await api.get(
            `/api/court/get-courtrooms/${establishment._id}`,
          );

          return [
            establishment._id,
            getRows<CourtRoomRow>(response).length,
          ] as const;
        }),
      );

      return Object.fromEntries(entries) as Record<string, number>;
    },
  );

  return (
    <div>
      <BackButton
        label={`Back to ${district.districtName}`}
        onClick={onBack}
      />
      <DetailHeader
        icon={<LayoutGrid className="h-6 w-6" />}
        title={complex.complexName}
        meta={
          <>
            <span className="inline-flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5" />
              {locationSummary(complex)}
            </span>
            <span className="inline-flex items-center gap-1">
              <LayoutGrid className="h-3.5 w-3.5" />
              {district.districtName}
            </span>
          </>
        }
        badges={
          complex.courtEstablishment
            ? [
                <span
                  key="has-establishments"
                  className="rounded-full border border-[#fcc09a] bg-[#fff4ee] px-2.5 py-1 text-xs font-semibold text-[#f26422]"
                >
                  Has Establishments
                </span>,
              ]
            : []
        }
        stats={[{ label: "Establishments", value: establishments.length }]}
      />

      <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-slate-500">
          Judicial establishments inside this complex. Click an establishment
          to manage its courtrooms.
        </p>
        <PrimaryButton label="Add Establishment" onClick={onAdd} className="w-full sm:w-auto" />
      </div>

      <DataTable
        headers={[
          "Establishment Name",
          "Type",
          "Presiding Officer",
          "Status",
          "Actions",
        ]}
        isLoading={isLoading}
        emptyLabel="No court establishments yet"
        emptyAction="Add Establishment"
        onEmptyAction={onAdd}
        mobileCards={establishments.map((establishment) => {
          const courtRoomCount =
            courtRoomCounts[establishment._id] ??
            establishment.courtRoomsCount ??
            0;

          return (
            <article
              key={establishment._id}
              className="rounded-xl border border-[#E8EDF5] bg-white p-4 shadow-sm"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-start gap-3">
                  <OrangeIconBox small>
                    <Home className="h-5 w-5" />
                  </OrangeIconBox>
                  <div className="min-w-0">
                    <button
                      type="button"
                      onClick={() => onOpen(establishment)}
                      className="text-left font-semibold text-[#f26422]"
                    >
                      {establishment.name}
                      <ArrowRight className="ml-1 inline h-4 w-4 text-[#f9a07a]" />
                    </button>
                    <div className="text-xs text-slate-400">
                      {plural(courtRoomCount, "courtroom")}
                    </div>
                  </div>
                </div>
                <RowActions
                  onEdit={() => onEdit(establishment)}
                  onDelete={() => onDelete(establishment)}
                />
              </div>
              <div className="mt-4 space-y-3">
                <MobileInfoRow
                  label="Type"
                  value={formatType(establishment)}
                />
                <MobileInfoRow
                  label="Presiding Officer"
                  value={presidingOfficer(establishment)}
                />
                <MobileInfoRow
                  label="Status"
                  value={<StatusBadge status={establishment.status || "Active"} />}
                />
              </div>
            </article>
          );
        })}
      >
        {establishments.map((establishment) => {
          const courtRoomCount =
            courtRoomCounts[establishment._id] ??
            establishment.courtRoomsCount ??
            0;

          return (
            <tr key={establishment._id} className={rowClassName}>
              <td className="px-5 py-4">
                <div className="flex items-center gap-3">
                  <OrangeIconBox small>
                    <Home className="h-5 w-5" />
                  </OrangeIconBox>
                  <div>
                    <button
                      type="button"
                      onClick={() => onOpen(establishment)}
                      className="text-left font-semibold text-[#f26422]"
                    >
                      {establishment.name}
                      <ArrowRight className="ml-1 inline h-4 w-4 text-[#f9a07a]" />
                    </button>
                    <div className="text-xs text-slate-400">
                      {plural(courtRoomCount, "courtroom")}
                    </div>
                  </div>
                </div>
              </td>
              <td className="px-5 py-4 text-sm text-slate-700">
                {formatType(establishment)}
              </td>
              <td className="px-5 py-4 text-sm text-slate-700">
                {presidingOfficer(establishment)}
              </td>
              <td className="px-5 py-4">
                <StatusBadge status={establishment.status || "Active"} />
              </td>
              <td className="px-5 py-4">
                <RowActions
                  onEdit={() => onEdit(establishment)}
                  onDelete={() => onDelete(establishment)}
                />
              </td>
            </tr>
          );
        })}
      </DataTable>
    </div>
  );
};
